// Dashboard kinh doanh cho quản trị: tổng quan hành trình khách hàng + 4 nhóm
// (1) Tổng chi phí marketing (2) Trực page (3) Telesale (4) Sale cơ sở.
// Một bộ lọc thời gian + chi nhánh dùng chung, mọi chỉ số so với kỳ trước tương ứng.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, DatePicker, Segmented, Select, Spin, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { ArrowUp, Download, ExternalLink, RefreshCw } from 'lucide-react';
import Layout from '../../Layout';
import {
  DEPT, MKT, RANGE_LABEL, bottleneck, campaignRows, fmtD, fmtFull, fmtMoney, fmtRate, insights, networkRows, pipeline, ratio, resolveRange, sourceMeta, totals,
  type Insight, type RangeKey,
} from './insights';
import { useSalesData } from './useSalesData';
import { SAMPLE_ALLOWED, SAMPLE_KEY } from './sampleMarketing';
import { MarketingGroup } from './MarketingGroup';
import { PageGroup, SaleGroup, TeleGroup } from './DeptGroups';
import { FlowSection } from './FlowSection';
import { ReconcilePanel } from './ReconcilePanel';
import { useYearTrends } from './useYearTrends';
import { Delta, LEVEL, Panel, SectionBoundary } from './ui';

const AUTO_REFRESH_MS = 5 * 60 * 1000;
const PRESETS = Object.keys(RANGE_LABEL) as Exclude<RangeKey, 'custom'>[];
const num = (v: number) => Math.round(v).toLocaleString('vi-VN');
const STEP_LABEL = { adMessages: 'Tin nhắn từ quảng cáo', leads: 'Khách hàng mới', booked: 'Lịch hẹn đã đặt', showup: 'Khách đến cơ sở', closed: 'Khách chốt' };

type GroupId = 'mkt' | 'page' | 'tele' | 'sale';
const GROUPS: { id: GroupId; anchor: string; index: number; label: string; color: string; soft: string }[] = [
  { id: 'mkt', anchor: 'nhom-marketing', index: 1, ...MKT, label: 'Chi phí marketing' },
  { id: 'page', anchor: 'nhom-truc-page', index: 2, ...DEPT.page },
  { id: 'tele', anchor: 'nhom-telesale', index: 3, ...DEPT.tele },
  { id: 'sale', anchor: 'nhom-sale-co-so', index: 4, ...DEPT.sale },
];
const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
const SECTIONS = ['tong-quan', ...GROUPS.map(g => g.anchor)];
// mép dưới thanh lọc dính trên màn hình rộng; mục nào cuộn qua mốc này là mục đang xem
const SPY_OFFSET = 180;
/** Khung đang cuộn chứa phần tử (Layout cuộn trong khung nội dung, không phải window) */
const scrollBoxOf = (el: Element | null): Element => {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(p).overflowY) && p.scrollHeight > p.clientHeight) return p;
  }
  return document.scrollingElement ?? document.documentElement;
};
const anchorOf = (id: GroupId) => GROUPS.find(g => g.id === id)!.anchor;
const COST_STEPS = ['leads', 'closed'];
const STEP_CARD = 'flex w-full flex-col rounded-xl p-3 text-left transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2';
const navCls = (on: boolean) => (on ? 'bg-slate-800 font-medium text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900');

export default function SalesDashboard() {
  const [params, setParams] = useSearchParams();
  const rangeKey = (params.get('range') as RangeKey) || 'month';
  const branch = params.get('branch') ? Number(params.get('branch')) : undefined;
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [active, setActive] = useState(SECTIONS[0]);
  const [showTop, setShowTop] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Trang cuộn trong khung nội dung của Layout (không phải window) → nghe scroll ở pha capture
  useEffect(() => {
    let frame = 0;
    const onScroll = (e: Event) => {
      const box = e.target instanceof Element ? e.target : document.scrollingElement;
      // bỏ qua các dải cuộn ngang bên trong trang
      if (!box || !topRef.current || !box.contains(topRef.current)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const passed = SECTIONS.filter(id => (document.getElementById(id)?.getBoundingClientRect().top ?? Infinity) <= SPY_OFFSET);
        // nhóm cuối ngắn, không cuộn tới được mốc → cuộn chạm đáy thì coi là đang xem nhóm cuối
        const atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 4;
        setActive(atBottom ? SECTIONS[SECTIONS.length - 1] : passed[passed.length - 1] ?? SECTIONS[0]);
        setShowTop((topRef.current?.getBoundingClientRect().top ?? 0) < -800);
      });
    };
    document.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('scroll', onScroll, true); cancelAnimationFrame(frame); };
  }, []);

  // màn hẹp menu nhóm cuộn ngang → kéo mục đang xem vào tầm nhìn (chỉ cuộn ngang, không đụng cuộn dọc của trang)
  useEffect(() => {
    const nav = navRef.current;
    const btn = nav?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!nav || !btn || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollTo({ left: btn.offsetLeft - (nav.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  }, [active]);

  // tính lại khi đổi ngày để "Hôm nay" không bị kẹt qua nửa đêm
  const today = dayjs().format('YYYY-MM-DD');
  const range = useMemo(() => resolveRange(rangeKey, params.get('from'), params.get('to')), [rangeKey, params, today]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sampleOn, setSampleOn] = useState(() => {
    try { return SAMPLE_ALLOWED && localStorage.getItem(SAMPLE_KEY) === '1'; } catch { return false; }
  });
  const toggleSample = (on: boolean) => {
    setSampleOn(on);
    try { localStorage.setItem(SAMPLE_KEY, on ? '1' : '0'); } catch { /* chế độ riêng tư */ }
  };
  const { cur, prev, loading, errors, updatedAt, branches, reload, mktEmpty, mktSample } = useSalesData(range, branch, sampleOn);
  const trends = useYearTrends(range.end.year());

  useEffect(() => {
    if (!autoRefresh || !range.live) return;
    const id = window.setInterval(reload, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, range.live, reload]);

  const setParam = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const c = useMemo(() => totals(cur), [cur]);
  const p = useMemo(() => totals(prev), [prev]);
  const steps = useMemo(() => pipeline(c, p), [c, p]);
  const prevSteps = useMemo(() => pipeline(p, p), [p]);
  const neck = useMemo(() => bottleneck(steps, prevSteps), [steps, prevSteps]);
  const alerts = useMemo(() => insights(cur, prev, range.prevLabel), [cur, prev, range.prevLabel]);
  const alertsOf = (g: GroupId) => alerts.filter(a => a.dept === g);
  const overall = ratio(c.closed, c.chats);
  const prevOverall = ratio(p.closed, p.chats);
  const per = (a: number, b: number) => (a > 0 && b > 0 ? a / b : null);

  const detailUrl = (tab: string) => {
    // giữ nguyên loại kỳ (tháng này, 7 ngày…) để báo cáo chi tiết so sánh cùng kỳ trước như dashboard
    const q = new URLSearchParams(rangeKey === 'custom' ? { tab, range: 'custom', from: fmtD(range.start), to: fmtD(range.end) } : { tab, range: rangeKey });
    if (branch) q.set('branch', String(branch));
    return `/marketing-report?${q.toString()}`;
  };
  const rangeText = range.start.isSame(range.end, 'day') ? range.start.format('DD/MM/YYYY') : `${range.start.format('DD/MM')} – ${range.end.format('DD/MM/YYYY')}`;
  const branchName = branches.find(b => b.id === branch)?.name;
  const firstLoad = loading && !updatedAt;
  const resetKey = updatedAt?.valueOf();
  const groupProps = { cur, prev, c, p, detailUrl, branchNote: !!branch, trends, range };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const add = (name: string, rows: Record<string, unknown>[]) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ '': 'Không có dữ liệu' }]), name);
    add('Tong quan', [
      ...steps.map(s => ({ 'Bộ phận': DEPT[s.dept].label, 'Chỉ số': s.label, 'Kỳ này': s.value, 'Kỳ trước': s.prev, 'Chuyển đổi từ bước trước (%)': s.conv === null ? '' : +s.conv.toFixed(1) })),
      { 'Bộ phận': DEPT.sale.label, 'Chỉ số': 'Doanh thu', 'Kỳ này': c.revenue, 'Kỳ trước': p.revenue },
      { 'Bộ phận': DEPT.sale.label, 'Chỉ số': 'Đơn trung bình', 'Kỳ này': c.aov, 'Kỳ trước': p.aov },
      { 'Bộ phận': MKT.label, 'Chỉ số': 'Chi phí quảng cáo', 'Kỳ này': c.spend, 'Kỳ trước': p.spend },
    ]);
    add('Can chu y', alerts.map(a => ({ 'Mức độ': LEVEL[a.level].label, 'Nhóm': sourceMeta(a.dept)?.label ?? 'Chung', 'Nội dung': a.title, 'Chi tiết': a.detail })));
    add('1 Mang quang cao', networkRows(cur.mkt).map(r => ({ 'Mạng': r.name, 'Đã chi': r.spent, 'Tỷ trọng (%)': +r.share.toFixed(1), 'Chiến dịch': r.campaigns, 'Đang chạy': r.activeCampaigns, 'Tin nhắn': r.messages, 'Khách hàng': r.leads, 'Chi phí/khách': r.cpl === null ? '' : Math.round(r.cpl), 'Doanh thu': r.revenue, 'ROAS': r.roas === null ? '' : +r.roas.toFixed(2) })));
    add('1 Chien dich', campaignRows(cur.mkt).map(r => ({ 'Chiến dịch': r.name, 'Kênh': r.channel, 'Trạng thái': r.active ? 'Đang chạy' : 'Đã dừng', 'Ngân sách': r.budget, 'Đã chi': r.spent, 'Tin nhắn': r.messages, 'Khách hàng': r.leads, 'Chi phí/tin nhắn': r.cpm === null ? '' : Math.round(r.cpm), 'Chi phí/khách': r.cpl === null ? '' : Math.round(r.cpl), 'Doanh thu': r.revenue, 'ROAS': r.roas === null ? '' : +r.roas.toFixed(2) })));
    add('1 Chi phi theo buoc', [{ 'Chỉ số': 'Chi phí quảng cáo', 'Kỳ này': c.spend, 'Kỳ trước': p.spend }, ...(['adMessages', 'leads', 'booked', 'showup', 'closed'] as const).map(k => ({ 'Chỉ số': STEP_LABEL[k], 'Kỳ này': c[k], 'Kỳ trước': p[k], 'Chi phí / người (kỳ này)': c.spend && c[k] ? Math.round(c.spend / c[k]) : '', 'Chi phí / người (kỳ trước)': p.spend && p[k] ? Math.round(p.spend / p[k]) : '' })), { 'Chỉ số': 'Doanh thu', 'Kỳ này': c.revenue, 'Kỳ trước': p.revenue }]);
    add('2 Truc page NV', (cur.page?.staff_customer_stats || []).map(s => ({ 'Nhân sự': s.staff_name, 'Khách nhắn tin': s.channel_customer_count, 'Khách hàng mới': s.customer_count, 'Tỷ lệ (%)': +(ratio(s.customer_count, s.channel_customer_count) ?? 0).toFixed(1) })));
    add('2 Fanpage', (cur.page?.customer_by_channels || []).map(x => ({ Fanpage: x.name, 'Số khách': x.count })));
    add('3 Telesale NV', (cur.tele?.rankings || []).map(s => ({ 'Nhân sự': s.name, 'Lịch đặt': s.booked, 'Khách đến': s.showup, 'Khách chốt': s.closed, 'Tỷ lệ đến (%)': s.showup_rate, 'Tỷ lệ chốt (%)': s.close_rate })));
    add('3 Ket qua cuoc goi', (cur.tele?.call_result_summary || []).map(x => ({ 'Kết quả': x.name, 'Số lượng': x.total, 'Tỷ lệ (%)': x.ratio })));
    add('4 Sale co so NV', (cur.sale?.rankings || []).map(s => ({ 'Nhân sự': s.name, 'Khách tiếp': s.showup, 'Khách chốt': s.closed, 'Tỷ lệ chốt (%)': s.rate, 'Doanh thu': s.revenue, 'Đơn trung bình': s.aov })));
    add('4 Dich vu', (cur.sale?.service_summary || []).map(x => ({ 'Mã': x.code, 'Dịch vụ': x.name, 'Số lượt': x.total, 'Doanh thu': x.revenue })));
    XLSX.writeFile(wb, `dashboard-kinh-doanh_${fmtD(range.start)}_${fmtD(range.end)}${mktSample ? '_marketing-du-lieu-mau' : ''}.xlsx`);
  };


  return (
    <Layout>
      <Helmet><title>Dashboard kinh doanh</title></Helmet>
      {/* full chiều ngang như các màn khác, không giới hạn bề rộng */}
      <div ref={topRef} className="w-full pb-8">
        {/* Tiêu đề */}
        {/* dưới xl tiêu đề và nút xếp 2 hàng, tránh nút bị rớt dòng lẻ trên desktop nhỏ / tablet */}
        <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-xl font-bold text-slate-800">
              Dashboard kinh doanh
              {range.live && (
                <span className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />Đang cập nhật
                </span>
              )}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Marketing · Trực page · Telesale · Sale cơ sở — <b className="font-medium text-slate-700">{rangeText}</b>{branchName ? ` · ${branchName}` : ''}
              <span className="text-slate-400"> · {range.prevLabel}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 xl:shrink-0 xl:flex-nowrap">
            <span className="hidden text-xs text-slate-400 sm:inline">{updatedAt ? `Cập nhật ${updatedAt.format('HH:mm')}` : ''}</span>
            {range.live && (
              <Tooltip title="Tự tải lại số liệu mỗi 5 phút khi kỳ xem có hôm nay">
                <span className="flex items-center gap-1.5 text-xs text-slate-500"><Switch size="small" checked={autoRefresh} onChange={setAutoRefresh} />Tự làm mới</span>
              </Tooltip>
            )}
            {/* màn hẹp chỉ hiện biểu tượng để các nút nằm gọn một hàng */}
            <Tooltip title="Làm mới"><Button aria-label="Làm mới" icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />} onClick={reload} disabled={loading}><span className="hidden sm:inline">Làm mới</span></Button></Tooltip>
            <Tooltip title="Xuất Excel"><Button aria-label="Xuất Excel" icon={<Download size={14} />} onClick={exportExcel} disabled={firstLoad}><span className="hidden sm:inline">Xuất Excel</span></Button></Tooltip>
            <Link to={detailUrl('page')}><Button icon={<ExternalLink size={14} />}><span className="hidden sm:inline">Báo cáo chi tiết</span><span className="sm:hidden">Chi tiết</span></Button></Link>
          </div>
        </div>

        {/* Bộ lọc dùng chung + chuyển nhanh giữa các nhóm */}
        {/* màn hẹp thanh lọc cao gần 1/4 màn hình nên chỉ dính từ md trở lên */}
        <div className="z-20 mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-2 backdrop-blur md:sticky md:top-[64px]">
          <div className="max-w-full overflow-x-auto">
            <Segmented<RangeKey>
              value={rangeKey}
              onChange={k => setParam({ range: k === 'month' ? undefined : k, from: undefined, to: undefined })}
              options={[...PRESETS.map(k => ({ value: k, label: RANGE_LABEL[k] })), ...(rangeKey === 'custom' ? [{ value: 'custom' as RangeKey, label: 'Tùy chọn' }] : [])]}
            />
          </div>
          <DatePicker.RangePicker
            value={[range.start, range.end]} format="DD/MM/YYYY" allowClear={false} className="w-full sm:w-auto"
            disabledDate={d => d.isAfter(dayjs(), 'day')}
            onChange={v => v?.[0] && v[1] && setParam({ range: 'custom', from: fmtD(v[0]), to: fmtD(v[1]) })}
          />
          <Select
            allowClear placeholder="Tất cả chi nhánh" className="w-full sm:ml-auto sm:w-56" value={branch}
            onChange={v => setParam({ branch: v ? String(v) : undefined })}
            options={branches.map(b => ({ value: b.id, label: b.name }))}
          />
          <nav ref={navRef} className="relative flex w-full gap-1 overflow-x-auto border-t border-slate-100 pt-1.5 text-[13px]">
            <button onClick={() => scrollTo('tong-quan')} aria-current={active === 'tong-quan' ? 'true' : undefined} className={`shrink-0 rounded-md px-2.5 py-1 ${navCls(active === 'tong-quan')}`}>Tổng quan</button>
            {GROUPS.map(g => (
              <button key={g.id} onClick={() => scrollTo(g.anchor)} aria-current={active === g.anchor ? 'true' : undefined} className={`flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 ${navCls(active === g.anchor)}`}>
                <span className="flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: g.color }}>{g.index}</span>{g.label}
                <AlertBadges alerts={alertsOf(g.id)} />
              </button>
            ))}
          </nav>
        </div>

        {errors.length > 0 && !loading && (
          <Alert
            className="mb-4" type={errors.length >= 4 ? 'error' : 'warning'} showIcon
            message={errors.length >= 4 ? 'Không tải được số liệu' : `Không tải được số liệu ${errors.join(', ')}`}
            description="Các phần còn lại vẫn hiển thị bình thường. Kiểm tra kết nối hoặc quyền truy cập rồi thử lại."
            action={<Button size="small" onClick={reload}>Thử lại</Button>}
          />
        )}

        <Spin spinning={firstLoad}>
          {/* ========== TỔNG QUAN ========== */}
          <section id="tong-quan" className="scroll-mt-4 md:scroll-mt-[150px]">
            {/* Dải số chính duy nhất của tổng quan (trước đây 4 thẻ tóm tắt lặp lại đúng các số này) */}
            <Panel
              className="mb-4" title="Hành trình khách hàng"
              info="Mỗi bước lấy từ bộ phận phụ trách. Tỷ lệ chuyển đổi = bước này / bước liền trước; có thể vượt 100% vì Telesale và Sale cơ sở còn xử lý khách cũ từ các kỳ trước. Bấm một ô để tới phần chi tiết của bộ phận đó."
              extra={<span className="hidden text-xs text-slate-400 sm:block">{(Object.keys(DEPT) as (keyof typeof DEPT)[]).map(k => (
                <span key={k} className="ml-3 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: DEPT[k].color }} />{DEPT[k].label}</span>
              ))}</span>}
            >
              <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4 xl:grid-cols-7">
                <button type="button" onClick={() => scrollTo(anchorOf('mkt'))} className={`${STEP_CARD} focus-visible:ring-slate-300`} style={{ background: MKT.soft }}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: MKT.color }}>{MKT.label}</div>
                  <div className="mt-0.5 truncate text-[13px] text-slate-600">Chi phí quảng cáo</div>
                  <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-2">
                    <Tooltip title={fmtFull(c.spend)}><span className="whitespace-nowrap text-2xl font-bold text-slate-800">{fmtMoney(c.spend)}</span></Tooltip>
                    <Delta cur={c.spend} prev={p.spend} neutral compact />
                  </div>
                  <div className="min-h-1.5 flex-1" />
                  <div className={`border-t border-white/80 pt-1.5 text-xs ${mktSample ? 'font-medium text-amber-700' : 'text-slate-500'}`}>{mktSample ? 'Dữ liệu mẫu' : 'Đầu vào'}</div>
                </button>
                {steps.map((s, i) => {
                  // chi phí quảng cáo / khách mới, / khách chốt (trước nằm ở thẻ Marketing)
                  const cost = COST_STEPS.includes(s.key) ? per(c.spend, s.value) : null;
                  return (
                  <Tooltip key={s.key} title={s.info}>
                    <button type="button" onClick={() => scrollTo(anchorOf(s.dept))} className={STEP_CARD} style={{ background: DEPT[s.dept].soft }}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: DEPT[s.dept].color }}>{DEPT[s.dept].label}</div>
                      <div className="mt-0.5 truncate text-[13px] text-slate-600">{s.label}</div>
                      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-2">
                        <span className="text-2xl font-bold text-slate-800">{num(s.value)}</span>
                        <Delta cur={s.value} prev={s.prev} compact />
                      </div>
                      {cost !== null && (
                        <div className="flex items-center justify-between gap-1 text-[11px] text-slate-500">
                          <span><b className="text-slate-700">{fmtMoney(cost)}</b>/khách</span>
                          <Delta cur={cost} prev={per(p.spend, prevSteps[i].value)} goodWhenUp={false} compact />
                        </div>
                      )}
                      <div className="min-h-1.5 flex-1" />
                      <div className="flex items-center justify-between gap-1 border-t border-white/80 pt-1.5 text-xs text-slate-500">
                        {i === 0 ? <span>{per(c.spend, s.value) ? `${fmtMoney(per(c.spend, s.value)!)}/khách` : 'Điểm bắt đầu'}</span> : (
                          <>
                            <span className={neck?.step.key === s.key ? 'font-semibold text-red-500' : ''}>→ {fmtRate(s.conv)}</span>
                            <Delta cur={s.conv} prev={prevSteps[i].conv} kind="points" compact />
                          </>
                        )}
                      </div>
                    </button>
                  </Tooltip>
                  );
                })}
                <button type="button" onClick={() => scrollTo(anchorOf('sale'))} className={`${STEP_CARD} col-span-2 bg-slate-800 text-white md:col-span-1`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-300">Kết quả</div>
                  <div className="mt-0.5 text-[13px] text-slate-300">Doanh thu</div>
                  <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-2">
                    <Tooltip title={fmtFull(c.revenue)}><span className="whitespace-nowrap text-2xl font-bold">{fmtMoney(c.revenue)}</span></Tooltip>
                    <span className="rounded bg-white/90 px-1"><Delta cur={c.revenue} prev={p.revenue} compact /></span>
                  </div>
                  <div className="min-h-1.5 flex-1" />
                  <div className="border-t border-white/10 pt-1.5 text-xs text-slate-300">
                    {c.spend > 0 ? `ROAS ${(c.revenue / c.spend).toFixed(1).replace('.', ',')}x` : `Đơn TB ${fmtMoney(c.aov)}`}
                  </div>
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px] text-slate-600">
                <span>Chuyển đổi toàn phễu (nhắn tin → chốt): <b className="text-slate-800">{fmtRate(overall)}</b> {overall !== null && <Delta cur={overall} prev={prevOverall} kind="points" />}</span>
                {neck
                  ? <span className="text-red-500">Điểm nghẽn: {neck.from} → {neck.step.label} giảm {neck.drop.toFixed(1).replace('.', ',')} điểm</span>
                  : <span className="text-slate-400">{prevSteps.some(s => s.value > 0) ? `Không có bước nào giảm đáng kể ${range.prevLabel}` : 'Kỳ trước chưa có số liệu để so sánh'}</span>}
              </div>
            </Panel>
            <SectionBoundary title="Dòng chảy & bàn giao" resetKey={resetKey}>
              <FlowSection cur={cur} prev={prev} range={range} />
            </SectionBoundary>
            <SectionBoundary title="Đối soát số liệu" resetKey={resetKey}>
              <ReconcilePanel cur={cur} c={c} />
            </SectionBoundary>
          </section>

          {/* ========== 4 NHÓM ========== */}
          <section id="nhom-marketing" className="mb-6 scroll-mt-4 md:scroll-mt-[150px]">
            <SectionBoundary title="Chi phí marketing" resetKey={resetKey}>
              <MarketingGroup cur={cur} prev={prev} c={c} p={p} alerts={alertsOf('mkt')} prevLabel={range.prevLabel} range={range} branchNote={!!branch}
                sample={SAMPLE_ALLOWED && mktEmpty ?{ on: sampleOn, active: mktSample, onToggle: toggleSample } : undefined} />
            </SectionBoundary>
          </section>
          <section id="nhom-truc-page" className="mb-6 scroll-mt-4 md:scroll-mt-[150px]">
            <SectionBoundary title="Trực page" resetKey={resetKey}>
              <PageGroup {...groupProps} alerts={alertsOf('page')} />
            </SectionBoundary>
          </section>
          <section id="nhom-telesale" className="mb-6 scroll-mt-4 md:scroll-mt-[150px]">
            <SectionBoundary title="Telesale" resetKey={resetKey}>
              <TeleGroup {...groupProps} alerts={alertsOf('tele')} />
            </SectionBoundary>
          </section>
          <section id="nhom-sale-co-so" className="scroll-mt-4 md:scroll-mt-[150px]">
            <SectionBoundary title="Sale cơ sở" resetKey={resetKey}>
              <SaleGroup {...groupProps} alerts={alertsOf('sale')} />
            </SectionBoundary>
          </section>
        </Spin>

        {showTop && (
          <Tooltip title="Lên đầu trang" placement="left">
            <button
              aria-label="Lên đầu trang" onClick={() => scrollBoxOf(topRef.current).scrollTo({ top: 0, behavior: 'smooth' })}
              className="fixed bottom-6 right-6 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md hover:text-[#357f96]"
            >
              <ArrowUp size={18} />
            </button>
          </Tooltip>
        )}
      </div>
    </Layout>
  );
}

/** Số cảnh báo nghiêm trọng / cần chú ý của một nhóm, hiện cạnh tên nhóm trên menu */
function AlertBadges({ alerts }: { alerts: Insight[] }) {
  return (
    <>
      {(['critical', 'warning'] as const).map(l => {
        const count = alerts.filter(a => a.level === l).length;
        if (!count) return null;
        const Icon = LEVEL[l].icon;
        return (
          <Tooltip key={l} title={`${count} ${LEVEL[l].label.toLowerCase()}`}>
            <span className={`flex items-center gap-0.5 text-[11px] font-semibold ${LEVEL[l].cls}`}><Icon size={12} />{count}</span>
          </Tooltip>
        );
      })}
    </>
  );
}
